import { useCallback, useEffect, useState } from 'react';
import {
  activeSlice,
  emptyInterests,
  INTERESTS_STORAGE_KEY,
  INTERESTS_STORAGE_KEY_V1,
  isProfileId,
  parseInterests,
  profileLabel,
  serializeInterests,
  toggleId,
  type InterestsV2,
  type ProfileId,
} from '@/lib/interests';

function readStore(): InterestsV2 {
  if (typeof localStorage === 'undefined') return emptyInterests();
  const v2 = localStorage.getItem(INTERESTS_STORAGE_KEY);
  const v1 = localStorage.getItem(INTERESTS_STORAGE_KEY_V1);
  const parsed = parseInterests(v2, v1);
  // Persist the migrated blob so a later v1 leftover cannot overwrite Lisa.
  if (!v2) writeStore(parsed);
  return parsed;
}

function writeStore(state: InterestsV2): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INTERESTS_STORAGE_KEY, serializeInterests(state));
}

/**
 * Per-device household Favorites (Chip / Lisa). Syncs across tabs on
 * this origin via the storage event. Never talks to the Worker.
 */
export function useInterests() {
  const [state, setState] = useState<InterestsV2>(emptyInterests);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setState(readStore());
    setLoaded(true);
    const onStorage = (e: StorageEvent) => {
      if (e.key === INTERESTS_STORAGE_KEY || e.key === INTERESTS_STORAGE_KEY_V1) {
        setState(parseInterests(e.newValue, localStorage.getItem(INTERESTS_STORAGE_KEY_V1)));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const commit = useCallback((next: InterestsV2) => {
    setState(next);
    writeStore(next);
  }, []);

  const setActiveProfile = useCallback(
    (id: ProfileId) => {
      if (!isProfileId(id)) return;
      commit({ ...readStore(), activeProfile: id });
    },
    [commit],
  );

  const toggleInterest = useCallback(
    (id: string) => {
      const current = readStore();
      const slice = activeSlice(current);
      commit({
        ...current,
        profiles: {
          ...current.profiles,
          [current.activeProfile]: {
            ...slice,
            interestIds: toggleId(slice.interestIds, id),
          },
        },
      });
    },
    [commit],
  );

  const togglePlace = useCallback(
    (slug: string) => {
      const current = readStore();
      const slice = activeSlice(current);
      commit({
        ...current,
        profiles: {
          ...current.profiles,
          [current.activeProfile]: {
            ...slice,
            placeSlugs: toggleId(slice.placeSlugs, slug),
          },
        },
      });
    },
    [commit],
  );

  const slice = activeSlice(state);

  return {
    loaded,
    activeProfile: state.activeProfile,
    displayName: profileLabel(state.activeProfile),
    setActiveProfile,
    interestIds: slice.interestIds,
    placeSlugs: slice.placeSlugs,
    hasInterest: (id: string) => slice.interestIds.includes(id),
    hasPlace: (slug: string) => slice.placeSlugs.includes(slug),
    toggleInterest,
    togglePlace,
  };
}
